import { PropsWithChildren, ReactElement, useMemo } from "react";

import { defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { TitleCard } from "./components/card";
import { RowGrid } from "./layouts/row-grid";

type WhyAtlassianData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon: ReactElement;
};

const icons = {
    integrations: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="25"
            viewBox="0 0 87 87"
            fill="none"
        >
            <path
                d="M22.1203 8.43994C16.1556 8.43994 11.3203 13.2753 11.3203 19.2399C11.3203 23.6686 13.986 27.4747 17.8003 29.1413L17.8003 73.2399H26.4403V45.8649C28.4043 46.7979 30.6013 47.3199 32.9203 47.3199H48.0403C52.812 47.3199 56.6803 51.1882 56.6803 55.9599V56.8586C52.866 58.5252 50.2003 62.3313 50.2003 66.7599C50.2003 72.7246 55.0356 77.5599 61.0003 77.5599C66.965 77.5599 71.8003 72.7246 71.8003 66.7599C71.8003 62.3312 69.1347 58.5252 65.3203 56.8586V55.9599C65.3203 46.4165 57.5838 38.6799 48.0403 38.6799H32.9203C29.3436 38.6799 26.4438 35.7822 26.4403 32.2063V29.1413C30.2547 27.4747 32.9203 23.6686 32.9203 19.2399C32.9203 13.2753 28.085 8.43994 22.1203 8.43994Z"
                fill="#82B536"
            />
        </svg>
    ),
    customers: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
        >
            <path
                d="M7.24943 17.0323C7.24943 14.0392 9.67576 11.6129 12.6688 11.6129C15.6618 11.6129 18.0881 14.0392 18.0881 17.0323V24H7.24943V17.0323Z"
                fill="#BF63F3"
            />
            <path
                d="M17.3139 4.64516C17.3139 7.21061 15.2342 9.29032 12.6688 9.29032C10.1033 9.29032 8.02363 7.21061 8.02363 4.64516C8.02363 2.07971 10.1033 0 12.6688 0C15.2342 0 17.3139 2.07971 17.3139 4.64516Z"
                fill="#BF63F3"
            />
            <path
                d="M17.9424 9.19925C18.2398 9.25898 18.5474 9.29032 18.8623 9.29032C21.4278 9.29032 23.5075 7.21061 23.5075 4.64516C23.5075 2.07971 21.4278 0 18.8623 0C18.5474 0 18.2398 0.0313426 17.9424 0.0910776C18.998 1.31238 19.6365 2.90421 19.6365 4.64516C19.6365 6.38612 18.998 7.97794 17.9424 9.19925Z"
                fill="#BF63F3"
            />
            <path
                d="M7.39514 0.0910776C6.33954 1.31238 5.70105 2.90421 5.70105 4.64516C5.70105 6.38612 6.33954 7.97794 7.39514 9.19925C7.09779 9.25898 6.79018 9.29032 6.47524 9.29032C3.90979 9.29032 1.83008 7.21061 1.83008 4.64516C1.83008 2.07971 3.90979 0 6.47524 0C6.79018 0 7.09779 0.0313426 7.39514 0.0910776Z"
                fill="#BF63F3"
            />
            <path
                d="M7.13994 11.6129C5.77097 13.0094 4.92685 14.9222 4.92685 17.0323V24H1.83008V15.4839C1.83008 13.346 3.56317 11.6129 5.70105 11.6129H7.13994Z"
                fill="#BF63F3"
            />
            <path
                d="M20.4107 24V17.0323C20.4107 14.9222 19.5666 13.0094 18.1976 11.6129H19.6365C21.7744 11.6129 23.5075 13.346 23.5075 15.4839V24H20.4107Z"
                fill="#BF63F3"
            />
        </svg>
    ),
    fedramp: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
        >
            <g clipPath="url(#clip0_1069_15578)">
                <path
                    d="M4.21547 1H0.659912V23.2222H4.21547V1Z"
                    fill="#FFA900"
                />
                <path
                    d="M6.88213 15.2222V1H24.6599L19.3266 8.11111L24.6599 15.2222H6.88213Z"
                    fill="#FFA900"
                />
            </g>
            <defs>
                <clipPath id="clip0_1069_15578">
                    <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0.659912)"
                    />
                </clipPath>
            </defs>
        </svg>
    ),
    resilience: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
        >
            <g clipPath="url(#clip0_1069_15614)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.00021 13.9105L4.83712 16.7971L12.2823 12.0593L19.7275 16.7971L21.5644 13.9105L12.2823 8.00376L3.00021 13.9105Z"
                    fill="#BF63F3"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.00021 6.30681L4.83712 9.19338L12.2823 4.45554L19.7275 9.19338L21.5644 6.30681L12.2823 0.400024L3.00021 6.30681Z"
                    fill="#BF63F3"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 21.5135L4.83691 24.4L12.2821 19.6622L19.7273 24.4L21.5642 21.5135L12.2821 15.6067L3 21.5135Z"
                    fill="#BF63F3"
                />
            </g>
            <defs>
                <clipPath id="clip0_1069_15614">
                    <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.400024)"
                    />
                </clipPath>
            </defs>
        </svg>
    ),
    platform: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
        >
            <g clipPath="url(#clip0_1069_15650)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24.8301 18.8286V24.4H14.1158V18.8286H24.8301ZM24.8301 16.2572V11.1143H14.1158V16.2572H24.8301ZM24.8301 8.54288V0.400024H0.830078V8.54288H24.8301ZM0.830078 11.1143V16.2572H11.5444V11.1143H0.830078ZM0.830078 18.8286H11.5444V24.4H0.830078V18.8286Z"
                    fill="#FFA900"
                />
            </g>
            <defs>
                <clipPath id="clip0_1069_15650">
                    <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0.830078 0.400024)"
                    />
                </clipPath>
            </defs>
        </svg>
    ),
    trustCenter: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
        >
            <g clipPath="url(#clip0_1069_15668)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22.8704 6.71581L12.7652 0.400024L2.65991 6.71581V18.0842L12.7652 24.4L22.8704 18.0842V6.71581ZM12.7652 16.009C14.7584 16.009 16.3742 14.3932 16.3742 12.4C16.3742 10.4068 14.7584 8.791 12.7652 8.791C10.772 8.791 9.15615 10.4068 9.15615 12.4C9.15615 14.3932 10.772 16.009 12.7652 16.009Z"
                    fill="#82B536"
                />
            </g>
            <defs>
                <clipPath id="clip0_1069_15668">
                    <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0.659912 0.400024)"
                    />
                </clipPath>
            </defs>
        </svg>
    )
} as const;

function useWhyAtlassianData(): Array<WhyAtlassianData> {
    return useMemo(() => {
        return [
            {
                id: "teams.integrations",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.integrations.title",
                    defaultMessage: "Integrations"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.integrations.content",
                    defaultMessage:
                        "Connect thousands of apps to your Atlassian products"
                }),
                href: "#",
                icon: icons.integrations
            },
            {
                id: "teams.customers",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.customers.title",
                    defaultMessage: "Customers"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.customers.content",
                    defaultMessage: "Case studies & stories powered by teamwork"
                }),
                href: "#",
                icon: icons.customers
            },
            {
                id: "teams.fedramp",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.fedramp.title",
                    defaultMessage: "FEDRamp"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.fedramp.content",
                    defaultMessage: "Compliant solutions for the public sector"
                }),
                href: "#",
                icon: icons.fedramp
            },
            {
                id: "teams.resilience",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.resilience.title",
                    defaultMessage: "Resilience"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.resilience.content",
                    defaultMessage:
                        "Enterprise-grade & highly performant infrastructure"
                }),
                href: "#",
                icon: icons.resilience
            },
            {
                id: "teams.platform",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.platform.title",
                    defaultMessage: "Platform"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.platform.content",
                    defaultMessage:
                        "Our deeply integrated, reliable & secure platform"
                }),
                href: "#",
                icon: icons.platform
            },
            {
                id: "teams.trust-center",
                title: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.trustcenter.title",
                    defaultMessage: "Trust center"
                }),
                content: defineMessage({
                    id: "globalnavv2.subnav.whyatlassian.trustcenter.content",
                    defaultMessage:
                        "Our deeply integrated, reliable & secure platform"
                }),
                href: "#",
                icon: icons.trustCenter
            }
        ];
    }, []);
}

const WhyAtlassianGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={3}
            rowGap={token("space.300")}
            columnGap={token("space.200")}
            paddingBlock={token("space.400")}
            paddingInline={token("space.300")}
        >
            {children}
        </RowGrid>
    );
};

export const WhyAtlassianSubnav = () => {
    const intl = useIntl();

    const teamsData = useWhyAtlassianData();

    return (
        <TypographyStyles>
            <WhyAtlassianGrid>
                {teamsData.map(({ id, title, content, ...rest }) => (
                    <TitleCard
                        key={id}
                        title={intl.formatMessage(title)}
                        content={intl.formatMessage(content)}
                        {...rest}
                    />
                ))}
            </WhyAtlassianGrid>
        </TypographyStyles>
    );
};
